import type { LinkProps } from 'next/link';
import { addRegionPrefix } from 'components/shared/CustomLink';
import { getPerformanceLabLenis } from 'utils/performanceLabLenisRegistry';

const IN_REGION_CODE = 'IN';
const IN_REGION_SLUG = 'in';
const IN_REGION_SLUG_PATTERN = /^in(?:-|$)/i;
const BUY_PATH = '/performance-lab/buy';
export type PerformanceLabBuyCategory =
  | 'recovery'
  | 'standalone'
  | 'iv-therapy'
  | 'diagnostics';

const PERFORMANCE_LAB_BUY_CATEGORIES: PerformanceLabBuyCategory[] = [
  'recovery',
  'standalone',
  'iv-therapy',
  'diagnostics',
];

export const resolvePerformanceLabBuyCategory = (
  queryCategory?: string,
  sectionId?: string,
): PerformanceLabBuyCategory | undefined => {
  if (
    queryCategory &&
    PERFORMANCE_LAB_BUY_CATEGORIES.includes(
      queryCategory as PerformanceLabBuyCategory,
    )
  ) {
    return queryCategory as PerformanceLabBuyCategory;
  }

  if (
    sectionId &&
    PERFORMANCE_LAB_BUY_CATEGORIES.includes(
      sectionId as PerformanceLabBuyCategory,
    )
  ) {
    return sectionId as PerformanceLabBuyCategory;
  }

  return undefined;
};

export type PerformanceLabBuyHrefOptions = {
  category?: PerformanceLabBuyCategory;
  planId?: string;
};

export const getPerformanceLabBuyHref = (
  options?: PerformanceLabBuyHrefOptions,
): LinkProps['href'] => {
  const query: Record<string, string> = {};

  if (options?.category) {
    query.category = options.category;
  }

  if (options?.planId) {
    query.plan = options.planId;
  }

  return Object.keys(query).length > 0
    ? { pathname: BUY_PATH, query }
    : BUY_PATH;
};

export const getPerformanceLabRecoveryBuyHref = (): LinkProps['href'] =>
  getPerformanceLabBuyHref({ category: 'recovery' });

export const getPerformanceLabIvTherapyBuyHref = (): LinkProps['href'] =>
  getPerformanceLabBuyHref({ category: 'iv-therapy' });

/** Viewport offset for sticky content below the Performance Lab navbar. */
export const PL_NAVBAR_OFFSET_CSS_VAR = '--pl-navbar-offset';
export const PL_NAVBAR_OFFSET_FALLBACK_PX = 56;
export const PL_NAVBAR_OFFSET_FALLBACK = `${PL_NAVBAR_OFFSET_FALLBACK_PX}px`;
export const PL_NAVBAR_HIDE_TRANSITION_MS = 300;
export const PL_NAVBAR_MORPH_TRANSITION_MS = 360;

/** Matches framed hero `MediaFrame` padding — navbar sits at the inner card top. */
export const PL_HERO_FRAME_PAD_MOBILE_PX = 12;
export const PL_HERO_FRAME_PAD_DESKTOP_PX = 24;
/** Extra inline inset so nav controls clear the framed hero card edges. */
export const PL_HERO_NAV_INLINE_PAD_MOBILE_PX =
  PL_HERO_FRAME_PAD_MOBILE_PX + 16;
export const PL_HERO_NAV_INLINE_PAD_DESKTOP_PX =
  PL_HERO_FRAME_PAD_DESKTOP_PX + 16;

const PL_NAV_HERO_MORPH_SCROLL_OFF = 8;
const PL_NAV_HERO_MORPH_SCROLL_ON = 40;
const PL_NAV_DESKTOP_MIN_WIDTH = 768;

/** 0 = hero inset padding, 1 = scrolled standard padding. */
export const getPerformanceLabHeroNavMorphProgress = (
  scrollY: number,
): number => {
  if (scrollY <= PL_NAV_HERO_MORPH_SCROLL_OFF) return 0;
  if (scrollY >= PL_NAV_HERO_MORPH_SCROLL_ON) return 1;
  return (
    (scrollY - PL_NAV_HERO_MORPH_SCROLL_OFF) /
    (PL_NAV_HERO_MORPH_SCROLL_ON - PL_NAV_HERO_MORPH_SCROLL_OFF)
  );
};

/** Interpolates navbar horizontal padding between hero inset and scrolled values. */
export const getPerformanceLabHeroNavHorizontalPadPx = (
  progress: number,
  viewportWidth: number,
): number => {
  const t = Math.min(1, Math.max(0, progress));
  if (viewportWidth < PL_NAV_DESKTOP_MIN_WIDTH) {
    const inset = PL_HERO_NAV_INLINE_PAD_MOBILE_PX;
    const outset = 16;
    return inset + (outset - inset) * t;
  }
  const inset = Math.min(
    96,
    Math.max(PL_HERO_NAV_INLINE_PAD_DESKTOP_PX, viewportWidth * 0.05 + 16),
  );
  const outset = Math.min(80, Math.max(24, viewportWidth * 0.05));
  return inset + (outset - inset) * t;
};

export const syncPerformanceLabNavbarOffset = (
  navEl: HTMLElement | null,
  contentEl?: HTMLElement | null,
) => {
  const rootBottom = navEl?.getBoundingClientRect().bottom ?? 0;
  const contentBottom = contentEl?.getBoundingClientRect().bottom ?? 0;
  const bottom = Math.ceil(Math.max(rootBottom, contentBottom, 0));

  document.documentElement.style.setProperty(
    PL_NAVBAR_OFFSET_CSS_VAR,
    `${bottom || PL_NAVBAR_OFFSET_FALLBACK_PX}px`,
  );
};

export const isIndiaRegion = (regionCode: string, regionSlug?: string) => {
  const isInRegion = regionCode.toUpperCase() === IN_REGION_CODE;
  const isInSlug = Boolean(regionSlug && IN_REGION_SLUG_PATTERN.test(regionSlug));
  return isInRegion || isInSlug;
};

export const withIndiaRegionPrefix = (href: LinkProps['href']): LinkProps['href'] => {
  if (typeof href === 'string') {
    return addRegionPrefix(href, IN_REGION_SLUG);
  }

  if (href.pathname) {
    return {
      ...href,
      pathname: addRegionPrefix(href.pathname, IN_REGION_SLUG),
    };
  }

  return href;
};

/** Primary PL book/explore destination — India store pricing for all regions. */
export const getPerformanceLabCtaPath = (
  regionCode: string,
  regionSlug?: string,
  options?: PerformanceLabBuyHrefOptions,
): LinkProps['href'] => {
  const buyHref = getPerformanceLabBuyHref(options);

  if (isIndiaRegion(regionCode, regionSlug)) {
    return buyHref;
  }

  return withIndiaRegionPrefix(buyHref);
};

type LenisScrollTarget = {
  scrollTo: (
    target: Element | string | number,
    options?: { offset?: number },
  ) => void;
};

const getPerformanceLabNavbarScrollOffset = (): number => {
  if (typeof window === 'undefined') {
    return -(PL_NAVBAR_OFFSET_FALLBACK_PX + 16);
  }

  const raw = getComputedStyle(document.documentElement).getPropertyValue(
    PL_NAVBAR_OFFSET_CSS_VAR,
  );
  const navbarOffset = Number.parseInt(raw, 10);
  const resolvedOffset = Number.isFinite(navbarOffset)
    ? navbarOffset
    : PL_NAVBAR_OFFSET_FALLBACK_PX;

  return -(resolvedOffset + 16);
};

/** Scroll to an in-page anchor, accounting for the fixed PL navbar and Lenis. */
export const scrollToPerformanceLabSection = (elementId: string): void => {
  const target = document.getElementById(elementId);
  if (!target) return;

  const offset = getPerformanceLabNavbarScrollOffset();
  const lenis = getPerformanceLabLenis() as LenisScrollTarget | null;

  if (lenis?.scrollTo) {
    lenis.scrollTo(target, { offset });
    return;
  }

  const top =
    target.getBoundingClientRect().top + window.scrollY + offset;
  window.scrollTo({ top, behavior: 'smooth' });
};
